/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, PublicClientApplication, EventType, AccountInfo, LocalStorage } from '@azure/msal-browser';
import { loadRecords } from './connection';
import { TimedCache } from '../models/TimedCache';
import { User, UserRole } from '../models/User';
import { isLocalHost, runningOnRobot } from './host-aware';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: any = {
  auth: {
    clientId: process.env.REACT_APP_APP_CLIENT_ID as string,
    authority: `https://login.microsoftonline.${process.env.REACT_APP_TLD}/${process.env.REACT_APP_TENANT_NAME}.onmicrosoft.${process.env.REACT_APP_TLD}`,
    redirectUri: isLocalHost() ? 'http://localhost:3000' : 'https://www.techmage.us',
    postLogoutRedirectUri: 'https://techmage.us',
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          // case LogLevel.Error:
          //   console.error(message);
          //   return;
          // case LogLevel.Info:
          //   console.info(message);
          //   return;
          // case LogLevel.Verbose:
          //   console.debug(message);
          //   return;
          // case LogLevel.Warning:
          //   console.warn(message);
          //   return;
          default:
            return;
        }
      }
    }
  }
};


const accountTimedCache = new TimedCache<AccountInfo>(1000 * 60 * 60);

export const msalInstance: any = runningOnRobot() ? {} : new PublicClientApplication(msalConfig);
if (!runningOnRobot()) {
  // Default to using the first account if no account is active on page load
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    const firstAccount = msalInstance.getAllAccounts()[0];
    msalInstance.setActiveAccount(firstAccount);
    accountTimedCache.set('activeAccount', firstAccount);
  }

  // Listen for sign-in event and set active account
  msalInstance.addEventCallback((event: any) => {
    // console.log('Event:', event.eventType);
    if (event.eventType === EventType.HANDLE_REDIRECT_END) {
      // console.log('removing loading');
      // localStorage.removeItem('loading');
    }
    if (event.eventType === EventType.LOGIN_SUCCESS && (event.payload as any).account) {
      const account = (event.payload as any).account;
      msalInstance.setActiveAccount(account);
      accountTimedCache.set('activeAccount', account);
    }
  });
}
/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const functionTokenRequest = {
  scopes: [`api://${process.env.REACT_APP_FUNCTION_CLIENT_ID}/user_impersonation`]
};

export const loginRequest = {
  scopes: [
    'offline_access',
    'openid',
    'profile',
    'User.Read',
    'User.ReadBasic.All',
    'User.Read.All',
    'User.ReadWrite.All',
    'User.ManageIdentities.All',
    'Directory.ReadWrite.All',
    'Directory.Read.All'
  ]
};

export const userTokenRequest = {
  scopes: ['User.Read', 'openid', `api://${process.env.REACT_APP_FUNCTION_CLIENT_ID}/user_impersonation`, 'offline_access']
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: `https://graph.microsoft.${process.env.REACT_APP_TLD}/v1.0/me`
};

export enum TokenType {
  'Function',
  'User'
}

export enum MethodType {
  'Get' = 'GET',
  'Post' = 'POST',
  'Put' = 'PUT',
  'Delete' = 'DELETE'
}

export async function handleReauthenticate(callback: any) {
  try {
    await msalInstance.loginPopup({ scopes: [...loginRequest.scopes], loginHint: user?.email, extraQueryParameters: { prompt: 'login' } });
    if (callback && typeof callback === 'function') {
      await callback();
    }
  } catch (error) {
    console.log(error);
  }
}

export async function authWarmUp() {
  const account = getActiveAccount();
  if (!account) {
    return;
  }
  await bearerToken(TokenType.Function);
  await bearerToken(TokenType.User);
}

export function getActiveAccount(redirect: boolean = false) {
  const cachedAccount = accountTimedCache.get('activeAccount');
  if (cachedAccount) {
    // console.log('Using cached account');
    return cachedAccount;
  }
  const account = msalInstance.getActiveAccount();
  if (account) {
    accountTimedCache.set('activeAccount', account);
    // console.log('New account acquired, caching');
    return account;
  } else if (redirect) {
    // console.log('redirecting from location:', window.location.href);
    if (!window.location.href.endsWith('/')) {
      window.location.href = '/';
    }
  }

  return null;
}

export const tokenTimedCache = new TimedCache<string>(1000 * 60 * 60);

export async function bearerToken(tokenType: TokenType = TokenType.Function) {
  await msalInstance.initialize();
  const account = getActiveAccount();
  if (!account) {
    throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
  }

  const cacheKey = `${tokenType}`;
  const cachedToken = tokenTimedCache.get(cacheKey);
  if (cachedToken) {
    // console.log('Using cached token');
    return cachedToken;
  }

  const response = await msalInstance.acquireTokenSilent({
    ...(tokenType === TokenType.Function ? functionTokenRequest : userTokenRequest),
    account: account
  });

  tokenTimedCache.set(cacheKey, response.accessToken);
  console.log('New token acquired, caching');

  return response.accessToken;
}

export async function getUserInfo(token: string) {
  const headers = new Headers();
  const bearer = `Bearer ${token}`;
  // console.log({ bearer });
  headers.append('Authorization', bearer);
  const response = await fetch(`https://graph.microsoft.${process.env.REACT_APP_TLD}/beta/me`, { headers, method: 'GET' });
  const userInfo = await response.text();
  // console.log({ userInfo: JSON.parse(userInfo) });
  return JSON.parse(userInfo);
}

export async function getUsersInfo(token: string) {
  const headers = new Headers();
  const bearer = `Bearer ${token}`;
  console.log({ bearer });
  headers.append('Authorization', bearer);
  const response = await fetch(`https://graph.microsoft.${process.env.REACT_APP_TLD}/beta/users`, { headers, method: 'GET' });
  const users = await response.text();
  // console.log({ users: JSON.parse(users).value });
  return JSON.parse(users).value;
}

export async function updateUserInfo(userId: string) {
  const token = await bearerToken(TokenType.User);
  const headers = new Headers();
  const bearer = `Bearer ${token}`;
  headers.append('Authorization', bearer);
  headers.append('Content-Type', 'application/json');
  const response = await fetch(`https://graph.microsoft.${process.env.REACT_APP_TLD}/v1.0/users/${userId}`, {
    headers,
    method: 'DELETE'
  });
  const userInfo = await response.text();
  // console.log({ userInfo: JSON.parse(userInfo) });
  return JSON.parse(userInfo);
}

export function isAdmin() {
  const account = getActiveAccount();
  if (!account) {
    throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
  }
  if (!account.idTokenClaims) {
    throw Error('No claims available for the account! Verify an ID token was requested and that the scope requested is correct.');
  }

  const groups = account.idTokenClaims.groups as string[];
  return groups?.includes(process.env.REACT_APP_SYSTEM_ADMIN_GROUP_ID as string);
}

export async function userRole(authToken: string, userId: string) {
  const headers = new Headers();
  const bearer = `Bearer ${authToken}`;
  headers.append('Authorization', bearer);
  const response = await fetch(`https://graph.microsoft.${process.env.REACT_APP_TLD}/v1.0/users/${userId}/memberOf`, {
    headers,
    method: 'GET'
  });
  const data = await response.json();
  const groups = data.value.map((group: any) => group.id);

  return groups.includes(process.env.REACT_APP_SYSTEM_ADMIN_GROUP_ID as string)
    ? UserRole.SystemAdmin
    : groups.includes(process.env.REACT_APP_ORG_ADMIN_GROUP_ID as string)
      ? UserRole.OrgAdmin
      : groups.includes(process.env.REACT_APP_DEVELOPER_GROUP_ID as string)
        ? UserRole.Developer
        : UserRole.Operator;
}

export let user: User | undefined;

export async function getUser(): Promise<User | undefined> {
  try {
    const account = getActiveAccount();
    if (!account) {
      return;
    }
    if (user) {
      // TODO: check age of user? to refresh information
      return user;
    }
    const token = await bearerToken(TokenType.User);
    const userInfo = (await getUserInfo(token)) as any;
    // console.log({ userInfo });
    // console.log('User Info id:', userInfo.id);
    const dbUser = await loadRecords<User>('users', 'userId', userInfo.id);
    // console.log({ dbUser });
    user = dbUser[0];
    return user;
  } catch (error) {
    localStorage.clear();
    console.log('Error getting user:', error);
    return undefined;
  }
}
